import { ref, onMounted } from 'vue'
import { logger } from '@siclop/kstone-vue'
import { useRegisterSW } from 'virtual:pwa-register/vue'

/**
 * Composable for managing PWA updates
 */
export function usePwaUpdate() {
  const needRefresh = ref(false)
  const offlineReady = ref(false)
  const updateError = ref<Error | null>(null)
  const updating = ref(false)

  const {
    needRefresh: swNeedRefresh,
    offlineReady: swOfflineReady,
    updateServiceWorker
  } = useRegisterSW({
    immediate: true,
    onNeedRefresh() {
      needRefresh.value = true
    },
    onOfflineReady() {
      offlineReady.value = true
    },
    onRegisteredSW(swUrl, registration) {
      logger.info(`Service Worker at: ${swUrl}`)
      // Check for updates every hour
      if (registration) {
        setInterval(() => {
          registration.update()
        }, 60 * 60 * 1000)
      }
    },
    onRegisterError(error) {
      updateError.value = error
      logger.error('SW registration error:', error)
    }
  })

  // Sync refs with SW state
  onMounted(() => {
    needRefresh.value = swNeedRefresh.value
    offlineReady.value = swOfflineReady.value
  })

  /**
   * Apply the update and reload the page
   */
  const applyUpdate = async () => {
    if (updating.value) return
    updating.value = true
    try {
      await updateServiceWorker(true)
    } catch (error) {
      updateError.value = error as Error
      updating.value = false
    }
    // Note: updating stays true because page will reload
  }

  /**
   * Dismiss the update notification
   */
  const dismissUpdate = () => {
    needRefresh.value = false
  }

  /**
   * Dismiss the offline ready notification
   */
  const dismissOfflineReady = () => {
    offlineReady.value = false
  }

  return {
    needRefresh,
    offlineReady,
    updateError,
    updating,
    applyUpdate,
    dismissUpdate,
    dismissOfflineReady
  }
}
